'use client';

import { useState, useEffect, useCallback, useRef } from 'react';
import { clientProfileApi } from '../api';
import { apiClient } from '@/sdk/shared/apiClient';
import { useLiveSync, invalidateCrmData } from '@/lib/live-sync';
import type { 
  ClientProfile, 
  WalletInfo, 
  TradingAccount, 
  KycDocument, 
  UpdateProfileRequest, 
  ChangePasswordRequest,
  AccountLimitsInfo
} from '../types';

interface UseProfileReturn {
  profile: ClientProfile | null;
  wallet: WalletInfo | null;
  tradingAccounts: TradingAccount[];
  kycDocuments: KycDocument[];
  accountLimits?: AccountLimitsInfo;
  isLoading: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
  updateProfile: (data: UpdateProfileRequest | FormData) => Promise<void>;
  uploadProfilePhoto: (file: File) => Promise<void>;
  changePassword: (data: ChangePasswordRequest) => Promise<void>;
}

export function useProfile(): UseProfileReturn {
  const [profile, setProfile] = useState<ClientProfile | null>(null);
  const [wallet, setWallet] = useState<WalletInfo | null>(null);
  const [tradingAccounts, setTradingAccounts] = useState<TradingAccount[]>([]);
  const [kycDocuments, setKycDocuments] = useState<KycDocument[]>([]);
  const [accountLimits, setAccountLimits] = useState<AccountLimitsInfo | undefined>(undefined);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const esRef = useRef<EventSource | null>(null);

  const fetchProfile = useCallback(async () => {
    try {
      setError(null);
      const result = await clientProfileApi.getProfile();
      if (result.success && result.data) {
        setProfile(result.data.client);
        setWallet(result.data.wallet);
        setTradingAccounts(result.data.tradingAccounts || []);
        setKycDocuments(result.data.kycDocuments || []);
        setAccountLimits(result.data.accountLimits);
      } else {
        throw new Error('Failed to load profile details');
      }
    } catch (err: any) {
      setError(err instanceof Error ? err : new Error(err?.message || 'Failed to fetch client profile'));
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Hook into live sync: background resume, iOS pageshow, window focus, online reconnect & scope invalidation
  useLiveSync({
    onSync: fetchProfile,
    scope: ['profile', 'wallet', 'accounts'],
    intervalMs: 20000,
  });

  // Manage SSE connection with proper iOS background/foreground lifecycle
  useEffect(() => {
    fetchProfile();

    const connectSSE = () => {
      const token = apiClient.getToken();
      if (!token) return;

      // Close any previous SSE connection before reconnecting
      if (esRef.current) {
        esRef.current.close();
        esRef.current = null;
      }

      let apiBase = apiClient.getApiURL();
      if (apiBase.startsWith('/')) {
        const protocol = window.location.protocol;
        const host = window.location.host;
        apiBase = `${protocol}//${host}${apiBase}`;
      }

      const sseUrl = `${apiBase}/client/profile/sse?token=${encodeURIComponent(token)}`;
      const es = new EventSource(sseUrl);
      esRef.current = es;

      es.onmessage = (event) => {
        try {
          const message = JSON.parse(event.data);
          if (message.type === 'update' && message.data) {
            if (message.data.tradingAccounts) {
              setTradingAccounts(message.data.tradingAccounts);
            }
            if (message.data.wallet) {
              setWallet(message.data.wallet);
            }
            // Propagate updates across other active hooks (e.g., useWalletBalance)
            invalidateCrmData(['wallet', 'accounts']);
          }
        } catch (err) {
          console.error('[Profile SSE] Error parsing update message:', err);
        }
      };

      es.onerror = (err) => {
        console.warn('[Profile SSE] EventSource encountered error, will reconnect if visible', err);
      };
    };

    connectSSE();

    // Reconnect SSE when user returns to app from iOS background
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        connectSSE();
      } else if (esRef.current) {
        esRef.current.close();
        esRef.current = null;
      }
    };

    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibility);
      if (esRef.current) {
        esRef.current.close();
        esRef.current = null;
      }
    };
  }, [fetchProfile]);

  const updateProfile = useCallback(
    async (data: UpdateProfileRequest | FormData) => {
      try {
        setIsLoading(true);
        const result = await clientProfileApi.updateProfile(data);
        if (result.success) {
          await fetchProfile(); // Refresh profile state
          invalidateCrmData(['profile', 'wallet', 'accounts']);
        } else {
          throw new Error(result.message || 'Failed to update profile');
        }
      } catch (err: any) {
        throw err instanceof Error ? err : new Error(err?.message || 'Failed to update profile');
      } finally {
        setIsLoading(false);
      }
    },
    [fetchProfile]
  );

  const uploadProfilePhoto = useCallback(
    async (file: File) => {
      try {
        setIsLoading(true);
        const formData = new FormData();
        formData.append('photo', file);
        const result = await clientProfileApi.uploadProfilePhoto(formData);
        if (!result.success) {
          throw new Error(result.message || 'Failed to upload profile photo');
        }
        await fetchProfile(); // Refresh profile state
        invalidateCrmData(['profile']);
      } catch (err: any) {
        throw err instanceof Error ? err : new Error(err?.message || 'Failed to upload profile photo');
      } finally {
        setIsLoading(false);
      }
    },
    [fetchProfile]
  );

  const changePassword = useCallback(
    async (data: ChangePasswordRequest) => {
      try {
        setIsLoading(true);
        const result = await clientProfileApi.changePassword(data);
        if (!result.success) {
          throw new Error(result.message || 'Failed to change password');
        }
      } catch (err: any) {
        throw err instanceof Error ? err : new Error(err?.message || 'Failed to change password');
      } finally {
        setIsLoading(false);
      }
    },
    []
  );

  return {
    profile,
    wallet,
    tradingAccounts,
    kycDocuments,
    accountLimits,
    isLoading,
    error,
    refetch: fetchProfile,
    updateProfile,
    uploadProfilePhoto,
    changePassword
  };
}
export default useProfile;
